/**
 * DanaVoice - Internationalization Translations
 *
 * Complete English and German translations for the DanaVoice dashboard.
 * German translations use formal "Sie" form, appropriate for nursing home staff.
 *
 * Key naming convention: section.descriptiveName (camelCase)
 */

export type Language = 'en' | 'de';

export const translations = {
  // ---------------------------------------------------------------------------
  // Language toggle (shows the OTHER language name)
  // ---------------------------------------------------------------------------
  'language.toggle': { en: 'Deutsch', de: 'English' },

  // ---------------------------------------------------------------------------
  // Common / shared strings
  // ---------------------------------------------------------------------------
  'common.appName': { en: 'Dana Voice', de: 'Dana Voice' },
  'common.virtualReceptionist': { en: 'Virtual Receptionist', de: 'Virtuelle Rezeption' },
  'common.logout': { en: 'Logout', de: 'Abmelden' },
  'common.loading': { en: 'Loading...', de: 'Wird geladen...' },
  'common.loadingNotifications': { en: 'Loading notifications...', de: 'Benachrichtigungen werden geladen...' },
  'common.error': { en: 'Error', de: 'Fehler' },
  'common.save': { en: 'Save', de: 'Speichern' },
  'common.cancel': { en: 'Cancel', de: 'Abbrechen' },
  'common.create': { en: 'Create', de: 'Erstellen' },
  'common.update': { en: 'Update', de: 'Aktualisieren' },
  'common.edit': { en: 'Edit', de: 'Bearbeiten' },
  'common.delete': { en: 'Delete', de: 'Loeschen' },
  'common.saving': { en: 'Saving...', de: 'Wird gespeichert...' },
  'common.view': { en: 'View', de: 'Ansehen' },
  'common.viewDetails': { en: 'View Details', de: 'Details ansehen' },
  'common.close': { en: 'Close', de: 'Schliessen' },
  'common.previous': { en: 'Previous', de: 'Zurueck' },
  'common.next': { en: 'Next', de: 'Weiter' },
  'common.user': { en: 'User', de: 'Benutzer' },
  'common.unknownCaller': { en: 'Unknown Caller', de: 'Unbekannter Anrufer' },
  'common.noDataAvailable': { en: 'No data available', de: 'Keine Daten verfuegbar' },
  'common.justNow': { en: 'Just now', de: 'Gerade eben' },
  'common.never': { en: 'Never', de: 'Nie' },
  'common.refreshData': { en: 'Refresh Data', de: 'Daten aktualisieren' },
  'common.dismiss': { en: 'Dismiss notification', de: 'Benachrichtigung schliessen' },
  'common.loadMore': { en: 'Load More', de: 'Mehr laden' },

  // ---------------------------------------------------------------------------
  // Time ago (relative time)
  // ---------------------------------------------------------------------------
  'time.dayAgo': { en: '{{count}} day ago', de: 'vor {{count}} Tag' },
  'time.daysAgo': { en: '{{count}} days ago', de: 'vor {{count}} Tagen' },
  'time.hourAgo': { en: '{{count}} hour ago', de: 'vor {{count}} Stunde' },
  'time.hoursAgo': { en: '{{count}} hours ago', de: 'vor {{count}} Stunden' },
  'time.minuteAgo': { en: '{{count}} minute ago', de: 'vor {{count}} Minute' },
  'time.minutesAgo': { en: '{{count}} minutes ago', de: 'vor {{count}} Minuten' },
  'time.minutesShort': { en: '{{count}}m ago', de: 'vor {{count}}m' },
  'time.hoursShort': { en: '{{count}}h ago', de: 'vor {{count}}h' },
  'time.daysShort': { en: '{{count}}d ago', de: 'vor {{count}}d' },

  // ---------------------------------------------------------------------------
  // Navigation items (DashboardLayout.tsx)
  // ---------------------------------------------------------------------------
  'nav.dashboard': { en: 'Dashboard', de: 'Dashboard' },
  'nav.analytics': { en: 'Analytics', de: 'Analysen' },
  'nav.callLogs': { en: 'Call Logs', de: 'Anrufprotokoll' },
  'nav.staff': { en: 'Staff', de: 'Personal' },
  'nav.settings': { en: 'Settings', de: 'Einstellungen' },
  // Disabled but kept for future use
  'nav.textMessages': { en: 'Text Messages', de: 'Textnachrichten' },
  'nav.residents': { en: 'Residents', de: 'Bewohner' },
  'nav.appointments': { en: 'Appointments', de: 'Termine' },
  'nav.faq': { en: 'FAQ', de: 'FAQ' },

  // ---------------------------------------------------------------------------
  // Dashboard page (Dashboard.tsx)
  // ---------------------------------------------------------------------------
  'dashboard.title': { en: 'Dashboard', de: 'Dashboard' },
  'dashboard.subtitle': { en: 'Manage notifications from incoming calls', de: 'Benachrichtigungen aus eingehenden Anrufen verwalten' },

  // Stats cards
  'dashboard.statistics': { en: 'Statistics', de: 'Statistiken' },
  'dashboard.totalNotifications': { en: 'Total Notifications', de: 'Benachrichtigungen gesamt' },
  'dashboard.unresolved': { en: 'Unresolved', de: 'Ungeloest' },
  'dashboard.resolved': { en: 'Resolved', de: 'Geloest' },

  // Priority breakdown
  'dashboard.priorityBreakdown': { en: 'Priority Breakdown', de: 'Prioritaetsuebersicht' },
  'dashboard.urgent': { en: 'Urgent', de: 'Dringend' },
  'dashboard.escalations': { en: 'Escalations', de: 'Eskalationen' },

  // Notification types
  'dashboard.notificationTypes': { en: 'Notification Types', de: 'Benachrichtigungstypen' },
  'dashboard.residentVisits': { en: 'Resident Visits', de: 'Bewohnerbesuche' },
  'dashboard.communityTours': { en: 'Community Tours', de: 'Einrichtungsfuehrungen' },

  // Filter tabs
  'dashboard.filterUnresolved': { en: 'Unresolved', de: 'Ungeloest' },
  'dashboard.filterAll': { en: 'All', de: 'Alle' },

  // Notification actions
  'dashboard.resolve': { en: 'Resolve', de: 'Erledigen' },
  'dashboard.unresolve': { en: 'Unresolve', de: 'Wiederoeffnen' },
  'dashboard.resolvedBadge': { en: 'Resolved', de: 'Erledigt' },
  'dashboard.viewCall': { en: 'View Call', de: 'Anruf ansehen' },

  // Empty states
  'dashboard.allCaughtUp': { en: 'All caught up!', de: 'Alles erledigt!' },
  'dashboard.noUnresolvedNotifications': { en: 'No unresolved notifications at the moment.', de: 'Derzeit keine ungeloesten Benachrichtigungen.' },
  'dashboard.noNotifications': { en: 'No notifications to display.', de: 'Keine Benachrichtigungen vorhanden.' },

  // Error / alert messages
  'dashboard.failedToLoadNotifications': { en: 'Failed to load notifications', de: 'Benachrichtigungen konnten nicht geladen werden' },
  'dashboard.failedToResolve': { en: 'Failed to resolve notification', de: 'Benachrichtigung konnte nicht erledigt werden' },
  'dashboard.failedToUnresolve': { en: 'Failed to unresolve notification', de: 'Benachrichtigung konnte nicht wiedergeoeffnet werden' },

  // Priority labels
  'priority.urgent': { en: 'URGENT', de: 'DRINGEND' },
  'priority.high': { en: 'HIGH', de: 'HOCH' },
  'priority.normal': { en: 'NORMAL', de: 'NORMAL' },
  'priority.low': { en: 'LOW', de: 'NIEDRIG' },

  // ---------------------------------------------------------------------------
  // Call Logs page (CallLogsPage.tsx)
  // ---------------------------------------------------------------------------
  'callLogs.title': { en: 'Call Logs', de: 'Anrufprotokoll' },
  'callLogs.totalCalls': { en: '{{total}} total calls', de: '{{total}} Anrufe gesamt' },
  'callLogs.showing': { en: 'Showing {{count}} of {{total}}', de: '{{count}} von {{total}} angezeigt' },

  // Filters
  'callLogs.callType': { en: 'Call Type', de: 'Anruftyp' },
  'callLogs.outcome': { en: 'Outcome', de: 'Ergebnis' },
  'callLogs.allTypes': { en: 'All Types', de: 'Alle Typen' },
  'callLogs.allOutcomes': { en: 'All Outcomes', de: 'Alle Ergebnisse' },

  // Call type options
  'callLogs.typeVoice': { en: 'Voice Calls', de: 'Sprachanrufe' },
  'callLogs.typeFaq': { en: 'FAQ', de: 'FAQ' },
  'callLogs.typeAppointment': { en: 'Appointment', de: 'Termin' },
  'callLogs.typeMessage': { en: 'Message', de: 'Nachricht' },
  'callLogs.typeEscalation': { en: 'Escalation', de: 'Eskalation' },
  'callLogs.typeOther': { en: 'Other', de: 'Sonstige' },

  // Outcome options
  'callLogs.outcomeAiHandled': { en: 'AI Handled', de: 'KI bearbeitet' },
  'callLogs.outcomeEscalated': { en: 'Escalated', de: 'Eskaliert' },
  'callLogs.outcomeMessageTaken': { en: 'Message Taken', de: 'Nachricht aufgenommen' },

  // Call detail sections
  'callLogs.aiSummary': { en: 'AI Summary', de: 'KI-Zusammenfassung' },
  'callLogs.noSummaryAvailable': { en: 'No summary available', de: 'Keine Zusammenfassung verfuegbar' },
  'callLogs.transcript': { en: 'Transcript', de: 'Transkript' },
  'callLogs.viewFullTranscript': { en: 'View Full Transcript', de: 'Vollstaendiges Transkript ansehen' },
  'callLogs.toolsUsed': { en: 'Tools Used', de: 'Verwendete Tools' },
  'callLogs.callTypeLabel': { en: 'Call Type', de: 'Anruftyp' },
  'callLogs.startedAt': { en: 'Started At', de: 'Begonnen um' },
  'callLogs.status': { en: 'Status', de: 'Status' },

  // Transcript modal
  'callLogs.callTranscript': { en: 'Call Transcript', de: 'Anruf-Transkript' },
  'callLogs.closeTranscript': { en: 'Close transcript', de: 'Transkript schliessen' },
  'callLogs.customer': { en: 'Customer', de: 'Anrufer' },
  'callLogs.aiAgent': { en: 'AI Agent', de: 'KI-Assistent' },

  // Audio player
  'callLogs.stopAudio': { en: 'Stop audio', de: 'Audio stoppen' },
  'callLogs.playAudio': { en: 'Play audio', de: 'Audio abspielen' },
  'callLogs.audioLoading': { en: 'Loading...', de: 'Wird geladen...' },
  'callLogs.audioPlaying': { en: 'Playing...', de: 'Wird abgespielt...' },
  'callLogs.audioPaused': { en: 'Paused', de: 'Pausiert' },
  'callLogs.pause': { en: 'Pause', de: 'Pause' },
  'callLogs.play': { en: 'Play', de: 'Abspielen' },

  // Default call titles
  'callLogs.voiceCall': { en: 'Voice Call', de: 'Sprachanruf' },

  // Empty state
  'callLogs.noCallLogs': { en: 'No call logs found', de: 'Keine Anrufe gefunden' },

  // Failed to load
  'callLogs.failedToLoad': { en: 'Failed to load calls', de: 'Anrufe konnten nicht geladen werden' },
  'callLogs.sessionExpired': { en: 'Your session has expired. Please sign in again.', de: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.' },

  // ---------------------------------------------------------------------------
  // Staff page (StaffPage.tsx)
  // ---------------------------------------------------------------------------
  'staff.title': { en: 'Staff Management', de: 'Personalverwaltung' },
  'staff.subtitle': { en: 'Manage staff accounts and permissions', de: 'Personalkonten und Berechtigungen verwalten' },
  'staff.addStaff': { en: '+ Add Staff Member', de: '+ Personal hinzufuegen' },

  // Table headers
  'staff.name': { en: 'Name', de: 'Name' },
  'staff.email': { en: 'Email', de: 'E-Mail' },
  'staff.role': { en: 'Role', de: 'Rolle' },
  'staff.lastLogin': { en: 'Last Login', de: 'Letzter Login' },
  'staff.status': { en: 'Status', de: 'Status' },
  'staff.actions': { en: 'Actions', de: 'Aktionen' },

  // Role badges
  'staff.roleAdministrator': { en: 'Administrator', de: 'Administrator' },
  'staff.roleStaff': { en: 'Staff', de: 'Mitarbeiter' },

  // Status badges
  'staff.statusActive': { en: 'Active', de: 'Aktiv' },
  'staff.statusInactive': { en: 'Inactive', de: 'Inaktiv' },

  // Actions
  'staff.deactivate': { en: 'Deactivate', de: 'Deaktivieren' },
  'staff.confirmDeactivate': { en: 'Are you sure you want to deactivate this staff member?', de: 'Moechten Sie dieses Teammitglied wirklich deaktivieren?' },
  'staff.failedToDeactivate': { en: 'Failed to deactivate staff member', de: 'Teammitglied konnte nicht deaktiviert werden' },
  'staff.activate': { en: 'Activate', de: 'Aktivieren' },
  'staff.confirmActivate': { en: 'Are you sure you want to reactivate this staff member?', de: 'Moechten Sie dieses Teammitglied wirklich reaktivieren?' },
  'staff.failedToActivate': { en: 'Failed to activate staff member', de: 'Teammitglied konnte nicht aktiviert werden' },

  // Empty state
  'staff.noStaffFound': { en: 'No staff members found', de: 'Keine Teammitglieder gefunden' },

  // Error state
  'staff.failedToLoad': { en: 'Failed to load staff members', de: 'Personal konnte nicht geladen werden' },
  'staff.failedToSave': { en: 'Failed to save staff member', de: 'Teammitglied konnte nicht gespeichert werden' },

  // Pagination
  'staff.page': { en: 'Page', de: 'Seite' },
  'staff.of': { en: 'of', de: 'von' },

  // Staff form modal
  'staff.editStaffMember': { en: 'Edit Staff Member', de: 'Teammitglied bearbeiten' },
  'staff.addNewStaffMember': { en: 'Add New Staff Member', de: 'Neues Teammitglied hinzufuegen' },
  'staff.fullName': { en: 'Full Name', de: 'Vollstaendiger Name' },
  'staff.emailLabel': { en: 'Email', de: 'E-Mail' },
  'staff.roleLabel': { en: 'Role', de: 'Rolle' },
  'staff.enableEmailNotifications': { en: 'Enable email notifications', de: 'E-Mail-Benachrichtigungen aktivieren' },
  'staff.activeStaffMember': { en: 'Active Staff Member', de: 'Aktives Teammitglied' },
  'staff.passwordNote': {
    en: 'The user will set their own password when they sign up using Stack Auth. Adding their email here whitelists them to create an account.',
    de: 'Der Benutzer legt sein eigenes Passwort fest, wenn er sich ueber Stack Auth registriert. Durch Hinzufuegen der E-Mail-Adresse wird die Kontoerstellung ermoeglicht.',
  },
  'staff.noteLabel': { en: 'Note:', de: 'Hinweis:' },
  'staff.locations': { en: 'Locations', de: 'Standorte' },
  'staff.locationsLabel': { en: 'Assigned Locations', de: 'Zugewiesene Standorte' },
  'staff.adminAllLocations': { en: 'Administrators have access to all locations. Selection is optional.', de: 'Administratoren haben Zugriff auf alle Standorte. Auswahl ist optional.' },
  'staff.accessDenied': { en: 'Access Denied', de: 'Zugriff verweigert' },
  'staff.adminOnlyPage': { en: 'Only administrators can access the staff management page.', de: 'Nur Administratoren koennen auf die Personalverwaltung zugreifen.' },

  // ---------------------------------------------------------------------------
  // Analytics page (AnalyticsPage.tsx)
  // ---------------------------------------------------------------------------
  'analytics.title': { en: 'Analytics Dashboard', de: 'Analyse-Dashboard' },
  'analytics.subtitle': { en: 'Call volume trends, performance metrics, and peak hour analysis', de: 'Anrufvolumen-Trends, Leistungskennzahlen und Stoßzeitenanalyse' },

  // Time period selector
  'analytics.timePeriod': { en: 'Time Period:', de: 'Zeitraum:' },
  'analytics.today': { en: 'Today', de: 'Heute' },
  'analytics.sevenDays': { en: '7 Days', de: '7 Tage' },
  'analytics.thirtyDays': { en: '30 Days', de: '30 Tage' },

  // Tabs
  'analytics.overview': { en: 'Overview', de: 'Uebersicht' },
  'analytics.detailedAnalysis': { en: 'Detailed Analysis', de: 'Detailanalyse' },
  'analytics.recentCalls': { en: 'Recent Calls', de: 'Letzte Anrufe' },

  // Performance metrics cards
  'analytics.totalCalls': { en: 'Total Calls', de: 'Anrufe gesamt' },
  'analytics.aiSuccessRate': { en: 'AI Success Rate', de: 'KI-Erfolgsrate' },
  'analytics.escalationRate': { en: 'Escalation Rate', de: 'Eskalationsrate' },
  'analytics.avgDuration': { en: 'Avg Duration', de: 'Durchschn. Dauer' },

  // Call volume summary
  'analytics.callVolumeSummary': { en: 'Call Volume Summary', de: 'Anrufvolumen-Zusammenfassung' },
  'analytics.aiHandled': { en: 'AI Handled', de: 'KI bearbeitet' },
  'analytics.escalated': { en: 'Escalated', de: 'Eskaliert' },
  'analytics.messages': { en: 'Messages', de: 'Nachrichten' },

  // Call types
  'analytics.callTypes': { en: 'Call Types', de: 'Anruftypen' },
  'analytics.calls': { en: 'calls', de: 'Anrufe' },

  // Detailed analysis - peak hours
  'analytics.peakHours': { en: 'Peak Hours', de: 'Stoßzeiten' },

  // Detailed analysis - performance details
  'analytics.performanceDetails': { en: 'Performance Details', de: 'Leistungsdetails' },
  'analytics.aiHandledCalls': { en: 'AI Handled Calls', de: 'KI-bearbeitete Anrufe' },
  'analytics.escalatedCalls': { en: 'Escalated Calls', de: 'Eskalierte Anrufe' },
  'analytics.messagesTaken': { en: 'Messages Taken', de: 'Aufgenommene Nachrichten' },
  'analytics.medianDuration': { en: 'Median Duration', de: 'Mittlere Dauer' },
  'analytics.totalTalkTime': { en: 'Total Talk Time', de: 'Gesamtgespraechszeit' },
  'analytics.minutes': { en: 'min', de: 'Min.' },

  // Recent calls table headers
  'analytics.time': { en: 'Time', de: 'Zeit' },
  'analytics.type': { en: 'Type', de: 'Typ' },
  'analytics.outcome': { en: 'Outcome', de: 'Ergebnis' },
  'analytics.duration': { en: 'Duration', de: 'Dauer' },
  'analytics.phone': { en: 'Phone', de: 'Telefon' },

  // Recent calls empty state
  'analytics.noRecentCalls': { en: 'No recent calls', de: 'Keine aktuellen Anrufe' },

  // ---------------------------------------------------------------------------
  // Settings page (SettingsPage.tsx)
  // ---------------------------------------------------------------------------
  'settings.title': { en: 'Settings', de: 'Einstellungen' },
  'settings.comingSoon': { en: 'Settings page - coming soon', de: 'Einstellungen - in Kuerze verfuegbar' },
  'settings.loggedInAs': { en: 'Logged in as:', de: 'Angemeldet als:' },

  // ---------------------------------------------------------------------------
  // Not Found page (NotFoundPage.tsx)
  // ---------------------------------------------------------------------------
  'notFound.code': { en: '404', de: '404' },
  'notFound.title': { en: 'Page Not Found', de: 'Seite nicht gefunden' },
  'notFound.message': {
    en: "The page you're looking for doesn't exist or has been moved.",
    de: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
  },
  'notFound.goToDashboard': { en: 'Go to Dashboard', de: 'Zum Dashboard' },
  'notFound.goBack': { en: 'Go Back', de: 'Zurueck' },

  // ---------------------------------------------------------------------------
  // Toast / Pusher notifications (ToastContext.tsx, DashboardLayout.tsx)
  // ---------------------------------------------------------------------------
  'toast.newCall': { en: 'New Call', de: 'Neuer Anruf' },
  'toast.newCallReceived': { en: 'New Call Received', de: 'Neuer Anruf eingegangen' },
  'toast.incomingCallFrom': { en: 'Incoming call from {{phone}}', de: 'Eingehender Anruf von {{phone}}' },
  'toast.newIncomingCall': { en: 'New incoming call', de: 'Neuer eingehender Anruf' },
  'toast.callEnded': { en: 'Call Ended', de: 'Anruf beendet' },
  'toast.callEndedFrom': { en: 'Call from {{phone}} has ended', de: 'Anruf von {{phone}} beendet' },
  'toast.callHasEnded': { en: 'Call has ended', de: 'Anruf beendet' },
  'toast.callEndedFromName': { en: 'Call from {{name}} has ended', de: 'Anruf von {{name}} beendet' },
  'toast.newAppointment': { en: 'New Appointment Scheduled', de: 'Neuer Termin vereinbart' },
  'toast.visitFor': { en: 'Visit for {{name}} at {{time}}', de: 'Besuch fuer {{name}} um {{time}}' },

  // ---------------------------------------------------------------------------
  // Outcome display labels (used across multiple pages)
  // ---------------------------------------------------------------------------
  'outcome.aiHandled': { en: 'AI Handled', de: 'KI bearbeitet' },
  'outcome.escalated': { en: 'Escalated', de: 'Eskaliert' },
  'outcome.messageTaken': { en: 'Message Taken', de: 'Nachricht aufgenommen' },

  // ---------------------------------------------------------------------------
  // Sentiment labels (CallLogsPage.tsx)
  // ---------------------------------------------------------------------------
  'sentiment.positive': { en: 'Positive', de: 'Positiv' },
  'sentiment.negative': { en: 'Negative', de: 'Negativ' },
  'sentiment.neutral': { en: 'Neutral', de: 'Neutral' },

  // ---------------------------------------------------------------------------
  // Call status labels (CallLogsPage.tsx metadata status)
  // ---------------------------------------------------------------------------
  'callStatus.done': { en: 'Done', de: 'Abgeschlossen' },
  'callStatus.completed': { en: 'Completed', de: 'Abgeschlossen' },
  'callStatus.failed': { en: 'Failed', de: 'Fehlgeschlagen' },
  'callStatus.error': { en: 'Error', de: 'Fehler' },
  'callStatus.inProgress': { en: 'In Progress', de: 'Laufend' },
  'callStatus.ringing': { en: 'Ringing', de: 'Klingelt' },

  // ---------------------------------------------------------------------------
  // Notification type labels (Dashboard.tsx)
  // ---------------------------------------------------------------------------
  'notificationType.residentVisitBooking': { en: 'Resident Visit Booking', de: 'Bewohnerbesuch-Buchung' },
  'notificationType.communityTourBooking': { en: 'Community Tour Booking', de: 'Einrichtungsfuehrung-Buchung' },
  'notificationType.messageLeft': { en: 'Message Left', de: 'Nachricht hinterlassen' },
  'notificationType.escalation': { en: 'Escalation', de: 'Eskalation' },
  'notificationType.other': { en: 'Other', de: 'Sonstige' },

  // ---------------------------------------------------------------------------
  // Access / auth-related
  // ---------------------------------------------------------------------------
  'access.restricted': { en: 'Access Restricted', de: 'Zugriff eingeschraenkt' },
  'access.notAssigned': { en: 'You have not been assigned access to this location.', de: 'Ihnen wurde kein Zugriff auf diesen Standort zugewiesen.' },
  'access.contactAdmin': { en: 'Please contact your administrator.', de: 'Bitte wenden Sie sich an Ihren Administrator.' },
  'access.signIn': { en: 'Sign In', de: 'Anmelden' },
  'access.signOut': { en: 'Sign Out', de: 'Abmelden' },

  // ---------------------------------------------------------------------------
  // Error messages (AnalyticsContext.tsx)
  // ---------------------------------------------------------------------------
  'errors.failedCallVolume': { en: 'Failed to load call volume data', de: 'Anrufvolumen-Daten konnten nicht geladen werden' },
  'errors.failedCallTypes': { en: 'Failed to load call types data', de: 'Anruftypen-Daten konnten nicht geladen werden' },
  'errors.failedPeakHours': { en: 'Failed to load peak hours data', de: 'Stoßzeiten-Daten konnten nicht geladen werden' },
  'errors.failedPerformance': { en: 'Failed to load performance metrics', de: 'Leistungskennzahlen konnten nicht geladen werden' },
  'errors.failedDashboardSummary': { en: 'Failed to load dashboard summary', de: 'Dashboard-Zusammenfassung konnte nicht geladen werden' },
  'errors.failedRecentCalls': { en: 'Failed to load recent calls', de: 'Letzte Anrufe konnten nicht geladen werden' },

  // ---------------------------------------------------------------------------
  // Form validation (generic)
  // ---------------------------------------------------------------------------
  'validation.required': { en: 'This field is required', de: 'Dieses Feld ist erforderlich' },
  'validation.invalidEmail': { en: 'Please enter a valid email address', de: 'Bitte geben Sie eine gueltige E-Mail-Adresse ein' },
} as const;

export type TranslationKey = keyof typeof translations;

/**
 * Retrieve a translated string for the given key and language.
 * Supports simple placeholder interpolation: {{placeholder}}.
 *
 * @example
 *   t('toast.incomingCallFrom', 'de', { phone: '+49 123 456' })
 *   // => 'Eingehender Anruf von +49 123 456'
 */
export function t(
  key: TranslationKey,
  lang: Language,
  params?: Record<string, string | number>,
): string {
  const entry = translations[key];
  if (!entry) {
    console.warn(`[i18n] Missing translation key: ${key}`);
    return key;
  }

  let text: string = entry[lang] ?? entry.en;

  if (params) {
    for (const [param, value] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), String(value));
    }
  }

  return text;
}
